import { useEffect, useMemo, useState } from 'react';
import { PencilSquareIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import SportBadge from '@/components/ui/SportBadge';
import Dropdown from '@/components/ui/Dropdown';
import SegmentedControl from '@/components/ui/SegmentedControl';
import EmptyState from '@/components/ui/EmptyState';
import Pagination from '@/components/ui/Pagination';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { BulkDeleteBar, SelectAllHeader, SelectRowCell } from '@/components/ui/BulkDelete';
import { useRowSelection } from '@/lib/useRowSelection';
import { useDebounce } from '@/lib/useDebounce';
import { useSports } from '@/features/reference/useReference';
import { deleteGuidance as deleteGuidanceRow, type Guidance } from '@/api/guidanceService';
import { useGuidance } from './useGuidance';
import GuidanceModal from './GuidanceModal';
import GuidanceImportModal from './GuidanceImportModal';
import CompetitionGuidanceTab from './CompetitionGuidanceTab';

const TABS = [
  { value: 'themes', label: 'Thèmes' },
  { value: 'competition', label: 'Compétition' },
];

const truncate = (value: string | null) => {
  if (!value) return '—';
  return value.length > 30 ? `${value.slice(0, 30)}…` : value;
};

const rowId = (g: Guidance) => `${g.themeId}:${g.sportIdentifier}`;

const GuidancePage = () => {
  const [tab, setTab] = useState('themes');
  const [search, setSearch] = useState('');
  const [sport, setSport] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sport]);

  const filters = useMemo(
    () => ({ search: debouncedSearch || undefined, sport: sport || undefined, page }),
    [debouncedSearch, sport, page]
  );

  const { data: sports = [] } = useSports();
  const { items, total, pageSize, isLoading, createGuidance, updateGuidance, deleteGuidance, isSaving, invalidate } =
    useGuidance(filters);
  const selection = useRowSelection(items, rowId, filters);

  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Guidance | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (guidance: Guidance) => {
    setEditing(guidance);
    setModalOpen(true);
  };

  const handleDelete = (guidance: Guidance) => {
    if (window.confirm(`Supprimer le contenu « ${guidance.themeLabel} / ${guidance.sportIdentifier} » ?`)) {
      deleteGuidance({ themeId: guidance.themeId, sportIdentifier: guidance.sportIdentifier });
    }
  };

  const tabs = (
    <div className="mb-4">
      <SegmentedControl value={tab} options={TABS} onChange={setTab} />
    </div>
  );

  if (tab === 'competition') {
    return (
      <>
        <PageHeader title="Mot du coach / nutrition / Warmup / Cooldown" description="Entrainement / Conseils" />
        {tabs}
        <CompetitionGuidanceTab />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Mot du coach / nutrition / Warmup / Cooldown"
        description="Entrainement / Conseils"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              Importer un CSV
            </Button>
            <Button onClick={openCreate}>+ Ajouter</Button>
          </div>
        }
      />

      {tabs}

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <Input
              label="Rechercher"
              placeholder="Thème ou contenu"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-48">
            <Select label="Sport" value={sport} onChange={(e) => setSport(e.target.value)}>
              <option value="">Tous</option>
              {sports.map((s) => (
                <option key={s.identifier} value={s.identifier}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
          <p className="ml-auto text-sm text-gray-500">{total} entrées</p>
        </div>
      </Card>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && items.length === 0 && (
        <EmptyState
          icon={<DocumentTextIcon />}
          title="Aucun contenu"
          description="Ajoutez ou importez le mot du coach et la nutrition par (thème, sport)."
        />
      )}

      {!isLoading && items.length > 0 && (
        <Card className="p-0">
          <BulkDeleteBar
            selection={selection}
            labelOf={(g) => `${g.themeLabel ?? '—'} / ${g.sportIdentifier}`}
            remove={(g) => deleteGuidanceRow(g.themeId, g.sportIdentifier)}
            onDone={invalidate}
          />
          <Table>
            <THead>
              <tr>
                <SelectAllHeader selection={selection} />
                <TH>Thème</TH>
                <TH>Sport</TH>
                <TH>Mot du coach</TH>
                <TH>Nutrition</TH>
                <TH>Echauffement</TH>
                <TH>Cooldown</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {items.map((guidance) => (
                <TR key={`${guidance.themeId}-${guidance.sportIdentifier}`}>
                  <SelectRowCell selection={selection} row={guidance} />
                  <TD className="font-bold text-gray-900 dark:text-gray-100">{guidance.themeLabel ?? '—'}</TD>
                  <TD>
                    <SportBadge identifier={guidance.sportIdentifier} />
                  </TD>
                  <TD className="text-gray-500" title={guidance.coachNote ?? ''}>
                    {truncate(guidance.coachNote)}
                  </TD>
                  <TD className="text-gray-500" title={guidance.nutrition ?? ''}>
                    {truncate(guidance.nutrition)}
                  </TD>
                  <TD className="text-gray-500" title={guidance.warmupPattern ?? ''}>
                    {truncate(guidance.warmupPattern) ?? '-'}
                  </TD>
                  <TD className="text-gray-500" title={guidance.cooldownPattern ?? ''}>
                    {truncate(guidance.cooldownPattern) ?? '-'}
                  </TD>
                  <TD className="text-right">
                    <Dropdown
                      actions={[
                        {
                          label: 'Modifier',
                          icon: <PencilSquareIcon className="h-4 w-4" />,
                          onClick: () => openEdit(guidance),
                        },
                        {
                          label: 'Supprimer',
                          icon: <TrashIcon className="h-4 w-4" />,
                          destructive: true,
                          onClick: () => handleDelete(guidance),
                        },
                      ]}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
        </Card>
      )}

      <GuidanceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        guidance={editing}
        isSaving={isSaving}
        onCreate={({ themeId, sportIdentifier, coachNote, nutrition, warmupPattern, cooldownPattern }) =>
          createGuidance({ themeId, sportIdentifier, coachNote, nutrition, warmupPattern, cooldownPattern })
        }
        onUpdate={({ themeId, sportIdentifier, coachNote, nutrition, warmupPattern, cooldownPattern }) =>
          updateGuidance({
            themeId,
            sportIdentifier,
            payload: { coachNote, nutrition, warmupPattern, cooldownPattern },
          })
        }
      />

      <GuidanceImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={invalidate} />
    </>
  );
};

export default GuidancePage;
